import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Warehouse as WarehouseIcon, Loader2, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import WarehousePage from "./Warehouse";

// Fully isolated, white-label portal for the 3rd-party Guangzhou warehouse.
// Only role "gudang_china" can enter; no brand, no consumer pages, no links out.
// Bilingual: Chinese (default) / Indonesian.
const L = {
  zh: { subtitle: "\u4ed3\u5e93\u5458\u5de5\u767b\u5f55", account: "\u8d26\u53f7", password: "\u5bc6\u7801", login: "\u767b\u5f55", logout: "\u9000\u51fa", welcome: "\u767b\u5f55\u6210\u529f\u3002", denied: "\u6b64\u8d26\u53f7\u65e0\u6743\u8bbf\u95ee\u4ed3\u5e93\u95e8\u6237\u3002" },
  id: { subtitle: "Login staf gudang", account: "Email / Akun", password: "Password", login: "Masuk", logout: "Keluar", welcome: "Berhasil masuk.", denied: "Akun ini tidak memiliki akses ke portal gudang." },
};

export default function WarehousePortal() {
  const { user, ready, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("wh_lang") || "zh");
  const t = L[lang] || L.zh;

  const setL = (l) => { setLang(l); localStorage.setItem("wh_lang", l); };
  useEffect(() => { document.title = "Warehouse Portal"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "gudang_china") {
        await logout();
        toast.error(t.denied);
        return;
      }
      toast.success(t.welcome);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const LangToggle = () => (
    <div className="inline-flex rounded-lg border border-stone-200 overflow-hidden text-xs font-semibold" data-testid="wh-lang-toggle">
      <button data-testid="wh-lang-zh" onClick={() => setL("zh")} className={`px-3 py-1.5 ${lang === "zh" ? "bg-sky-600 text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}>中文</button>
      <button data-testid="wh-lang-id" onClick={() => setL("id")} className={`px-3 py-1.5 ${lang === "id" ? "bg-sky-600 text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}>ID</button>
    </div>
  );

  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center bg-stone-50 text-sky-600"><Loader2 className="animate-spin" size={32} /></div>;
  }

  if (user && user.role === "gudang_china") {
    return (
      <div className="min-h-screen bg-stone-50">
        <header className="bg-white border-b border-stone-200">
          <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-display font-bold text-stone-800" data-testid="wh-portal-brand">
              <WarehouseIcon size={22} className="text-sky-600" /><span>Warehouse Portal · 云铺集运</span>
            </div>
            <div className="flex items-center gap-3">
              <LangToggle />
              <button data-testid="wh-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-red-600">
                <LogOut size={16} />{t.logout}
              </button>
            </div>
          </div>
        </header>
        <WarehousePage lang={lang} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-stone-100" data-testid="wh-portal-login">
      <div className="w-full max-w-md bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
        <div className="flex items-center justify-between mb-5">
          <div className="w-12 h-12 rounded-xl bg-sky-600 flex items-center justify-center text-white"><WarehouseIcon size={24} /></div>
          <LangToggle />
        </div>
        <h1 className="font-display text-2xl font-bold text-stone-900">仓库系统登录</h1>
        <p className="text-sm text-stone-500 mt-1">{t.subtitle}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-stone-600">{t.account}</label>
            <input data-testid="wh-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
          </div>
          <div>
            <label className="text-sm font-medium text-stone-600">{t.password}</label>
            <input data-testid="wh-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
          </div>
          <button data-testid="wh-login-submit" disabled={loading} className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />}{t.login}
          </button>
        </form>
      </div>
    </div>
  );
}
